import React from 'react';
import { useParams } from 'react-router-dom';

import { Accordion, Badge, Box, Divider, Grid, Group, Text } from '@mantine/core';
import dayjs from 'dayjs';

import { statusMap } from '@/store/selectors/transmissionReportSelectors';
import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import formatDate from '@/utils/formatDate';
import { neutral } from '@/styles/colors';

import { Lead, MatchDevice } from '@/types/matchDevice';
import IconButton from '@/ui/IconButton';
import LabeledField from '@/ui/LabeledField';

import { VENDOR_MAPPING } from '../../PatientProfilePage/components/PatientTabs/PatientDeviceScheduleTab/types';
import {
  getDeviceStatusColor,
  getDeviceStatusLabel
} from '../../TransmissionReportPage/CreateReport/Components/ManualTransmissionSection';
import DeviceAddModal from '../../TransmissionReportPage/DeviceInfo/DeviceAddModal';
import DeviceEditModal from '../../TransmissionReportPage/DeviceInfo/DeviceEditModal';

import styles from './Devices.module.scss';

const getDeviceFields = (device: MatchDevice) => [
  { label: 'Manufacturer', value: device.vendor?.toUpperCase() },
  { label: 'Type', value: device.type },
  { label: 'Model Name', value: device.modelName },
  { label: 'Model Number', value: device.modelNumber },
  { label: 'Serial Number', value: device.serialNumber },
  { label: 'Implant Date', value: formatDate(device.implantDate, 'MM/DD/YYYY') },
  { label: 'Device Telemetry', value: device.deviceTelemetry },
  {
    label: 'MRI Compatibility',
    value: device.isMriCompatibility == null ? '-' : device.isMriCompatibility ? 'Yes' : 'No'
  }
];

const getLeadFields = (lead: Lead) => {
  const fields = [
    {
      label: `Lead Manufacturer (${lead?.leadChamber})`,
      value: lead.leadManufacturer?.leadVendorName ?? '-'
    },
    { label: 'Lead Model', value: lead.leadModel ?? '-' },
    { label: 'Serial Number', value: lead.serialNumber ?? '-' },
    {
      label: 'Implant Date',
      value: lead.implantDate ? dayjs(lead.implantDate).format('MM/DD/YYYY') : '-'
    },
    { label: 'Lead Status', value: lead.leadStatus ?? '-' },
    { label: 'Lead Location', value: lead.leadLocation ?? '-' },
    {
      label: 'Lead Capped',
      value: lead.leadCapped === undefined ? '-' : lead.leadCapped ? 'Yes' : 'No'
    }
  ];
  return fields;
};

const renderFields = (fields: { label: string; value?: string | null }[]) => {
  return fields.map((field) => (
    <Grid.Col key={field.label} span={{ base: 12, sm: 6, md: 3 }}>
      <LabeledField label={field.label} value={field.value} />
    </Grid.Col>
  ));
};

interface DevicesProps {
  isDeviceModalOpen: boolean;
  setIsDeviceModalOpen: (open: boolean) => void;
}

const Devices: React.FC<DevicesProps> = ({ isDeviceModalOpen, setIsDeviceModalOpen }) => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const { data: patient, refetch } = useGetPatientQuery(patientId, {
    refetchOnMountOrArgChange: true
  });

  const [editModalOpened, setEditModalOpened] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState<MatchDevice | undefined>(undefined);
  const [openedAccordion, setOpenedAccordion] = React.useState<string | null>(null);

  const devices = patient?.matchDevices;

  const handleModalClose = () => {
    setEditModalOpened(false);
    refetch();
  };

  const handleAddModalClose = () => {
    setIsDeviceModalOpen(false);
    refetch();
  };

  if (!devices || devices.length === 0) {
    return (
      <>
        <DeviceAddModal
          isOpen={isDeviceModalOpen}
          onClose={handleAddModalClose}
          patientId={patientId}
          devices={patient?.matchDevices || []}
        />
        <Text size="sm">Patient has no active devices.</Text>
      </>
    );
  }

  return (
    <>
      <Accordion
        variant="contained"
        className={styles.deviceAccordion}
        chevronPosition="left"
        value={openedAccordion}
        onChange={setOpenedAccordion}>
        {devices?.map((device) => (
          <Accordion.Item
            value={String(device.id)}
            key={device.id}
            className={styles.deviceAccordionItem}>
            <Accordion.Control>
              <Group justify="space-between">
                <Text fw={500} className={styles.accTab}>
                  {VENDOR_MAPPING[device.vendor] ?? device.vendor} {device.type}
                  {device && (
                    <Badge
                      ml={15}
                      bg={getDeviceStatusColor(device.status as unknown as string) || neutral[3]}>
                      {getDeviceStatusLabel(device.status as unknown as string)}
                    </Badge>
                  )}
                </Text>
                {openedAccordion === String(device.id) && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      icon="edit"
                      label="Edit"
                      onClick={() => {
                        setSelectedDevice({
                          ...device,
                          status:
                            statusMap[device?.status as unknown as 0 | 1 | 2 | 3 | 4] ??
                            device?.status
                        });
                        setEditModalOpened(true);
                      }}
                    />
                  </div>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              {/* Device Details */}
              <Box mb="sm" mt={'5px'}>
                <Text fw={600}>Device Details</Text>
                <Divider my={4} />
                <Grid mt="md">{renderFields(getDeviceFields(device))}</Grid>
              </Box>
              {device?.type !== 'ILR' && (
                <>
                  <Box mt="sm">
                    <Divider my={4} />
                    <Text fw={600}>Active Leads</Text>
                    <Divider my={4} />
                    {device?.leads?.some(
                      (lead) => lead.leadStatus?.toLocaleLowerCase() === 'active'
                    ) ? (
                      device.leads
                        .filter((lead) => lead.leadStatus?.toLocaleLowerCase() === 'active')
                        .map((lead, idx) => (
                          <Grid mt="md" key={idx}>
                            {renderFields(getLeadFields(lead))}
                          </Grid>
                        ))
                    ) : (
                      <Text size="sm">No active leads</Text>
                    )}
                  </Box>
                  <Divider my={4} />
                  {/* Inactive Leads */}
                  <Box mt="sm">
                    <Text fw={600}>Inactive Leads</Text>
                    <Divider my={4} />
                    {device?.leads?.some(
                      (lead) => lead.leadStatus?.toLocaleLowerCase() === 'inactive'
                    ) ? (
                      device.leads
                        .filter((lead) => lead.leadStatus?.toLocaleLowerCase() === 'inactive')
                        .map((lead, idx) => (
                          <Grid mt="md" key={idx}>
                            {renderFields(getLeadFields(lead))}
                          </Grid>
                        ))
                    ) : (
                      <Text size="sm">No inactive leads</Text>
                    )}
                  </Box>
                </>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
      <DeviceEditModal
        isOpen={editModalOpened}
        onClose={handleModalClose}
        deviceInfo={selectedDevice}
        leadsData={selectedDevice?.leads || []}
        isPatientView={true}
      />
      <DeviceAddModal
        isOpen={isDeviceModalOpen}
        onClose={handleAddModalClose}
        patientId={patientId}
        devices={patient?.matchDevices || []}
      />
    </>
  );
};

export default Devices;
